import { inject, Injectable } from '@angular/core';
import { Polygon } from 'geojson';
import { LngLat, MapLayerMouseEvent, MapMouseEvent } from 'maplibre-gl';

import {
    getBoundsFromPoints,
    RectangleBounds,
    translateBounds,
} from '@connect-services/client/services/map/bbox-shaping/bbox-geometry.utils';
import {
    CornerKey,
    EdgeKey,
    MapBboxRenderService,
    SHAPING_LAYER_IDS,
} from '@connect-services/client/services/map/bbox-shaping/render/map-bbox-render.service';
import { MapModeService } from '@connect-services/client/services/map/mode/map-mode.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { TARGET_CURSOR } from '@connect-services/client/services/map-edit-mode.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';

const EDGE_CURSORS: Record<EdgeKey, string> = {
    bottom: 'ns-resize',
    left: 'ew-resize',
    right: 'ew-resize',
    top: 'ns-resize',
};

type MapConfiguration = {
    initialBounds: RectangleBounds | null;
    onClose: (bbox: Polygon | null) => void;
};

@Injectable({ providedIn: 'root' })
export class MapBboxShapingService extends MapBaseService {
    private activeCorner: CornerKey | null = null;
    private activeEdge: EdgeKey | null = null;
    private dragOrigin: LngLat | null = null;
    private hoveredEdgeKey: EdgeKey | null = null;
    private hoveredLayer: 'corner' | 'edge' | 'fill' | null = null;
    private isDragging = false;
    private isDrawing = false;
    private isResizing = false;
    private readonly mapBboxRenderService = inject(MapBboxRenderService);
    private readonly mapModalService = inject(MapModalService);
    private readonly mapModeService = inject(MapModeService);
    private rectangleBounds: RectangleBounds | null = null;
    private startPoint: LngLat | null = null;

    public cleanBboxShaping(): void {
        this.destroyMapEvents();
        this.cleanMap();
        this.mapModeService.isBboxShapingActive = false;
    }

    public clearBbox(): void {
        this.rectangleBounds = null;
        this.mapBboxRenderService.clearSource();
    }

    public initMap(options: MapConfiguration): void {
        if (!this.map) {
            return;
        }
        if (options.initialBounds) {
            this.rectangleBounds = options.initialBounds;
        }

        this.mapBboxRenderService.initShapeSourceAndLayers();

        if (this.rectangleBounds) {
            this.mapBboxRenderService.renderRectangleFromBounds(this.rectangleBounds);
        }

        this.map.getCanvas().style.cursor = TARGET_CURSOR;
        this.registerMapEvents();
        this.mapModalService.open(MapModal.SHAPING, {
            onClose: (isConfirm: boolean) => {
                if (!isConfirm) {
                    this.cleanBboxShaping();

                    return;
                }
                const bounds = this.rectangleBounds;
                const bbox: Polygon | null = bounds
                    ? {
                          coordinates: [
                              [
                                  [bounds.minLng, bounds.minLat],
                                  [bounds.maxLng, bounds.minLat],
                                  [bounds.maxLng, bounds.maxLat],
                                  [bounds.minLng, bounds.maxLat],
                                  [bounds.minLng, bounds.minLat],
                              ],
                          ],
                          type: 'Polygon',
                      }
                    : null;

                options.onClose(bbox);
                this.cleanBboxShaping();
            },
        });
    }

    private cleanMap(): void {
        this.mapBboxRenderService.cleanMap();
        this.rectangleBounds = null;
    }

    private destroyMapEvents(): void {
        if (!this.map) {
            return;
        }

        const { cornersLayer, edgesHitLayer, fillHitLayer } = SHAPING_LAYER_IDS;

        this.map.off('click', this.onMapClick);
        this.map.off('mousemove', this.onMapMouseMove);
        this.map.off('mouseup', this.onMapMouseUp);

        this.map.off('mousedown', fillHitLayer, this.onFillMouseDown);
        this.map.off('mouseenter', fillHitLayer, this.onFillMouseEnter);
        this.map.off('mouseleave', fillHitLayer, this.onFillMouseLeave);

        this.map.off('mousedown', edgesHitLayer, this.onEdgeMouseDown);
        this.map.off('mouseenter', edgesHitLayer, this.onEdgeMouseEnter);
        this.map.off('mouseleave', edgesHitLayer, this.onEdgeMouseLeave);

        this.map.off('mousedown', cornersLayer, this.onCornerMouseDown);
        this.map.off('mouseenter', cornersLayer, this.onCornerMouseEnter);
        this.map.off('mouseleave', cornersLayer, this.onCornerMouseLeave);
    }

    private dragRectangle(cursor: LngLat): void {
        if (!this.rectangleBounds || !this.dragOrigin) {
            return;
        }

        const lngOffset = cursor.lng - this.dragOrigin.lng;
        const latOffset = cursor.lat - this.dragOrigin.lat;

        this.rectangleBounds = translateBounds(this.rectangleBounds, lngOffset, latOffset);

        this.dragOrigin = cursor;
        this.mapBboxRenderService.renderRectangleFromBounds(this.rectangleBounds);
    }

    private finishDrawing(end: LngLat, shiftKey: boolean): void {
        const constrained = this.getConstrainedEnd(end, shiftKey);
        this.isDrawing = false;
        this.rectangleBounds = getBoundsFromPoints(this.startPoint!, constrained);
        this.mapBboxRenderService.renderRectangleFromBounds(this.rectangleBounds);
    }

    private getConstrainedEnd(end: LngLat, shiftKey: boolean): LngLat {
        if (!this.map || !this.startPoint || !shiftKey) {
            return end;
        }

        const endPixel = this.map.project(end);
        const startPixel = this.map.project(this.startPoint);
        const horizontalDistance = endPixel.x - startPixel.x;
        const verticalDistance = endPixel.y - startPixel.y;
        const size = Math.max(Math.abs(horizontalDistance), Math.abs(verticalDistance));

        return this.map.unproject([
            startPixel.x + (horizontalDistance === 0 ? 0 : Math.sign(horizontalDistance) * size),
            startPixel.y + (verticalDistance === 0 ? 0 : Math.sign(verticalDistance) * size),
        ]);
    }

    private getCornerKeyFromEvent(event: MapLayerMouseEvent): CornerKey | null {
        const key = event.features?.[0]?.properties?.['key'];
        const validKeys: CornerKey[] = ['bottomLeft', 'bottomRight', 'topLeft', 'topRight'];

        return validKeys.includes(key) ? key : null;
    }

    private getEdgeKeyFromEvent(event: MapLayerMouseEvent): EdgeKey | null {
        const key = event.features?.[0]?.properties?.['key'];
        const validKeys: EdgeKey[] = ['bottom', 'left', 'right', 'top'];

        return validKeys.includes(key) ? key : null;
    }

    private onCornerMouseDown = (event: MapLayerMouseEvent): void => {
        if (!this.map || !this.rectangleBounds || this.isDrawing) {
            return;
        }

        const key = this.getCornerKeyFromEvent(event);
        if (!key) {
            return;
        }

        event.preventDefault();
        this.startResizing({ corner: key });
    };

    private onCornerMouseEnter = (): void => {
        this.hoveredLayer = 'corner';
        this.refreshCursor();
    };

    private onCornerMouseLeave = (): void => {
        if (this.hoveredLayer === 'corner') {
            this.hoveredLayer = null;
        }
        this.refreshCursor();
    };

    private onEdgeMouseDown = (event: MapLayerMouseEvent): void => {
        if (!this.map || !this.rectangleBounds || this.isDrawing) {
            return;
        }

        const key = this.getEdgeKeyFromEvent(event);
        if (!key) {
            return;
        }

        event.preventDefault();
        this.startResizing({ edge: key });
    };

    private onEdgeMouseEnter = (event: MapLayerMouseEvent): void => {
        this.hoveredLayer = 'edge';
        this.hoveredEdgeKey = this.getEdgeKeyFromEvent(event);
        this.refreshCursor();
    };

    private onEdgeMouseLeave = (): void => {
        if (this.hoveredLayer === 'edge') {
            this.hoveredLayer = null;
            this.hoveredEdgeKey = null;
        }
        this.refreshCursor();
    };

    private onFillMouseDown = (event: MapLayerMouseEvent): void => {
        if (!this.map || !this.rectangleBounds || this.isDrawing) {
            return;
        }

        event.preventDefault();
        this.startDragging(event.lngLat);
    };

    private onFillMouseEnter = (): void => {
        this.hoveredLayer = 'fill';
        this.refreshCursor();
    };

    private onFillMouseLeave = (): void => {
        if (this.hoveredLayer === 'fill') {
            this.hoveredLayer = null;
        }
        this.refreshCursor();
    };

    private onMapClick = (event: MapMouseEvent): void => {
        if (!this.map || this.isResizing || this.isDragging) {
            return;
        }

        if (!this.isDrawing) {
            this.startDrawing(event.lngLat);

            return;
        }

        this.finishDrawing(event.lngLat, event.originalEvent.shiftKey);
    };

    private onMapMouseMove = (event: MapMouseEvent): void => {
        if (!this.map) {
            return;
        }

        if (this.isDragging) {
            this.dragRectangle(event.lngLat);

            return;
        }

        if (this.isResizing) {
            this.resizeRectangle(event.lngLat);

            return;
        }

        if (!(this.isDrawing && this.startPoint)) {
            return;
        }

        const end = this.getConstrainedEnd(event.lngLat, event.originalEvent.shiftKey);
        this.rectangleBounds = getBoundsFromPoints(this.startPoint, end);
        this.mapBboxRenderService.renderRectangleFromBounds(this.rectangleBounds);
    };

    private onMapMouseUp = (): void => {
        if (!this.map) {
            return;
        }

        this.activeCorner = null;
        this.activeEdge = null;
        this.dragOrigin = null;
        this.isResizing = false;
        this.isDragging = false;

        this.map.dragPan.enable();
        this.refreshCursor();
    };

    private refreshCursor(): void {
        if (!this.map) {
            return;
        }

        if (this.isDragging || this.isResizing) {
            this.map.getCanvas().style.cursor = 'grabbing';

            return;
        }

        if (this.isDrawing) {
            this.map.getCanvas().style.cursor = TARGET_CURSOR;

            return;
        }

        switch (this.hoveredLayer) {
            case 'corner':
                this.map.getCanvas().style.cursor = 'grab';
                break;
            case 'edge':
                this.map.getCanvas().style.cursor = this.hoveredEdgeKey
                    ? EDGE_CURSORS[this.hoveredEdgeKey]
                    : TARGET_CURSOR;
                break;
            case 'fill':
                this.map.getCanvas().style.cursor = 'move';
                break;
            default:
                this.map.getCanvas().style.cursor = TARGET_CURSOR;
        }
    }

    private registerMapEvents(): void {
        const { cornersLayer, edgesHitLayer, fillHitLayer } = SHAPING_LAYER_IDS;

        this.map!.on('click', this.onMapClick);
        this.map!.on('mousemove', this.onMapMouseMove);
        this.map!.on('mouseup', this.onMapMouseUp);

        this.map!.on('mousedown', fillHitLayer, this.onFillMouseDown);
        this.map!.on('mouseenter', fillHitLayer, this.onFillMouseEnter);
        this.map!.on('mouseleave', fillHitLayer, this.onFillMouseLeave);

        this.map!.on('mousedown', edgesHitLayer, this.onEdgeMouseDown);
        this.map!.on('mouseenter', edgesHitLayer, this.onEdgeMouseEnter);
        this.map!.on('mouseleave', edgesHitLayer, this.onEdgeMouseLeave);

        this.map!.on('mousedown', cornersLayer, this.onCornerMouseDown);
        this.map!.on('mouseenter', cornersLayer, this.onCornerMouseEnter);
        this.map!.on('mouseleave', cornersLayer, this.onCornerMouseLeave);
    }

    private resizeRectangle(cursor: LngLat): void {
        if ((!this.activeCorner && !this.activeEdge) || !this.rectangleBounds) {
            return;
        }

        if (this.activeCorner) {
            this.resizeRectangleFromCorner(cursor);

            return;
        }
        this.resizeRectangleFromEdge(cursor);
    }

    private resizeRectangleFromCorner(cursor: LngLat): void {
        if (!this.rectangleBounds || !this.activeCorner) {
            return;
        }

        const { minLng, maxLng, minLat, maxLat } = this.rectangleBounds;

        const anchorByCorner: Record<CornerKey, LngLat> = {
            bottomLeft: new LngLat(maxLng, maxLat),
            bottomRight: new LngLat(minLng, maxLat),
            topLeft: new LngLat(maxLng, minLat),
            topRight: new LngLat(minLng, minLat),
        };

        this.rectangleBounds = getBoundsFromPoints(anchorByCorner[this.activeCorner], cursor);
        this.mapBboxRenderService.renderRectangleFromBounds(this.rectangleBounds);
    }

    private resizeRectangleFromEdge(cursor: LngLat): void {
        if (!this.rectangleBounds || !this.activeEdge) {
            return;
        }

        const next = { ...this.rectangleBounds };

        switch (this.activeEdge) {
            case 'bottom':
                next.minLat = Math.min(cursor.lat, next.maxLat);
                break;
            case 'top':
                next.maxLat = Math.max(cursor.lat, next.minLat);
                break;
            case 'left':
                next.minLng = Math.min(cursor.lng, next.maxLng);
                break;
            case 'right':
                next.maxLng = Math.max(cursor.lng, next.minLng);
                break;
        }

        this.rectangleBounds = next;
        this.mapBboxRenderService.renderRectangleFromBounds(this.rectangleBounds);
    }

    private startDragging(origin: LngLat): void {
        this.isDragging = true;
        this.dragOrigin = origin;
        this.activeCorner = null;
        this.activeEdge = null;

        this.map!.dragPan.disable();
        this.refreshCursor();
    }

    private startDrawing(origin: LngLat): void {
        this.isDrawing = true;
        this.startPoint = origin;
        this.rectangleBounds = getBoundsFromPoints(origin, origin);
        this.mapBboxRenderService.renderRectangleFromBounds(this.rectangleBounds);
    }

    private startResizing(active: { corner: CornerKey } | { edge: EdgeKey }): void {
        if ('corner' in active) {
            this.activeCorner = active.corner;
            this.activeEdge = null;
        } else {
            this.activeCorner = null;
            this.activeEdge = active.edge;
        }

        this.isDrawing = false;
        this.isResizing = true;

        this.map!.dragPan.disable();
        this.refreshCursor();
    }
}
